// src/components/idcard/IdCard.tsx — the physical card, digital twin.
//
// A faithful reproduction of the NYMR PVC ID card. Renders LANDSCAPE on wide
// screens and PORTRAIT on phones; scales fluidly to its container (all internal
// sizing in `cqw` container units so type & spacing stay proportional). The
// magenta palette below is the CARD's own brand (sampled from the PVC card) and
// is deliberately separate from the hub's brass theme — the card is a twin of a
// physical object, not a hub surface. No expiry; QR encodes the 9-digit number.
//
// Ported from the prototype's idcard-card.jsx.

import type { CSSProperties } from "react";
import { groupCardNo, fmtDate, expiryLabel, cardDigits, hasCardNo } from "./format";
import { QrCode } from "./QrCode";

// Magenta sampled from the physical NYMR ID card.
const K = {
  pink: "#C2479A", // header band + frame
  pinkDk: "#9E3079", // frame shadow edge
  pinkLt: "#D873B6", // band highlight
  body: "#FCFAFB", // card face (near-white, faint warm)
  ink: "#1C1A1B", // card text
  inkSoft: "#5B545A", // label text
  logoInk: "#111111", // NYMR oval logo stroke
};

export interface CardHolder {
  name: string;
  /** The NYMR "ID No" (member_id_number), already stringified; "—" when unset. */
  idNo: string;
  /** Card number, digits-only ("" when none yet). */
  cardNo: string;
  /** Issue date — ISO string or null (⇒ "—"). */
  issueDate: string | null;
}

export type CardOrientation = "landscape" | "portrait";

// ─── NYMR oval logo — the official registered mark (public/logos/nymr-logo.svg) ───
const NymrOval = ({ w = "86px" }: { w?: string }) => (
  <img
    src="/logos/nymr-logo.svg"
    alt="North Yorkshire Moors Railway"
    style={{ display: "block", width: w, height: "auto" }}
  />
);

// ─── Contactless glyph (concentric arcs — as on the physical card) ───
const Contactless = ({ s = "26px", c = K.inkSoft }: { s?: string; c?: string }) => (
  <svg
    width={s}
    height={s}
    viewBox="0 0 24 24"
    fill="none"
    stroke={c}
    strokeWidth="1.7"
    strokeLinecap="round"
    style={{ display: "block" }}
  >
    <path d="M8 6a9 9 0 0 1 0 12" />
    <path d="M11.5 8a5.5 5.5 0 0 1 0 8" />
    <path d="M15 9.5a2.6 2.6 0 0 1 0 5" />
  </svg>
);

// ─── Photo box ───
const PhotoBox = ({ photo, radius = 10, style }: { photo: string | null; radius?: number; style?: CSSProperties }) => (
  <div
    style={{
      position: "relative",
      overflow: "hidden",
      borderRadius: radius,
      background: "linear-gradient(160deg,#ECE4EA,#D9CDD6)",
      boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)",
      ...style,
    }}
  >
    {photo ? (
      <img src={photo} alt="Cardholder" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
    ) : (
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "4%",
          color: "#9C8E97",
          textAlign: "center",
          padding: "8%",
        }}
      >
        <svg width="34%" height="34%" viewBox="0 0 24 24" fill="none" stroke="#B3A6AE" strokeWidth="1.4">
          <circle cx="12" cy="9" r="4" />
          <path d="M4 21c1.4-4 4.4-6 8-6s6.6 2 8 6" />
        </svg>
        <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: "clamp(7px,2.6cqw,11px)", fontWeight: 600, letterSpacing: 0.5 }}>
          ADD PHOTO
        </div>
      </div>
    )}
  </div>
);

// ─── A single field row ───
const Fld = ({ label, value, fs }: { label: string; value: string; fs: string }) => (
  <div style={{ display: "flex", alignItems: "baseline", gap: "0.5em", lineHeight: 1.25 }}>
    <span style={{ fontWeight: 800, color: K.ink, fontSize: fs, whiteSpace: "nowrap" }}>{label}</span>
    <span style={{ fontWeight: 600, color: K.inkSoft, fontSize: fs, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
      {value}
    </span>
  </div>
);

// gloss sheen overlay for PVC realism
const Sheen = () => (
  <div
    style={{
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      borderRadius: "inherit",
      background:
        "linear-gradient(120deg, rgba(255,255,255,0.30) 0%, rgba(255,255,255,0) 32%, rgba(255,255,255,0) 70%, rgba(255,255,255,0.16) 100%)",
    }}
  />
);

// ─── Header band ───
const Band = ({ fs }: { fs: string }) => (
  <div
    style={{
      background: `linear-gradient(180deg, ${K.pinkLt} 0%, ${K.pink} 55%, ${K.pinkDk} 100%)`,
      color: "#fff",
      textAlign: "center",
      fontFamily: "Arial, Helvetica, sans-serif",
      fontWeight: 800,
      letterSpacing: "0.22em",
      fontSize: fs,
      padding: "0.5em 0",
      textShadow: "0 1px 1px rgba(0,0,0,0.25)",
    }}
  >
    NYMR
  </div>
);

export interface IdCardProps {
  holder: CardHolder;
  photo: string | null;
  orientation?: CardOrientation;
  /** Cap on card width (px). Card keeps CR80 aspect (1.585). */
  maxW?: number;
  onEnlarge?: () => void;
}

export function IdCard({ holder, photo, orientation = "landscape", maxW, onEnlarge }: IdCardProps) {
  const qrVal = cardDigits(holder.cardNo);
  const land = orientation === "landscape";
  const cap = maxW ?? (land ? 580 : 360);

  const frame: CSSProperties = {
    position: "relative",
    width: "100%",
    maxWidth: cap,
    aspectRatio: land ? "1.585 / 1" : "1 / 1.585",
    background: `linear-gradient(150deg, ${K.pinkLt}, ${K.pink} 45%, ${K.pinkDk})`,
    borderRadius: "min(4.5cqw, 26px)",
    padding: land ? "1.7%" : "2.7%",
    boxShadow:
      "0 1px 0 rgba(255,255,255,0.4) inset, 0 18px 40px -12px rgba(60,16,46,0.45), 0 2px 8px rgba(60,16,46,0.25)",
    containerType: "inline-size",
    boxSizing: "border-box",
    cursor: onEnlarge ? "zoom-in" : "default",
  };
  const face: CSSProperties = {
    position: "relative",
    width: "100%",
    height: "100%",
    background: K.body,
    borderRadius: "min(3cqw,16px)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 1px 3px rgba(60,16,46,0.18)",
  };

  // ─── LANDSCAPE ───
  if (land) {
    return (
      <div style={frame} onClick={onEnlarge} title={onEnlarge ? "Tap to enlarge" : undefined}>
        <div style={face}>
          <Band fs="clamp(13px,3.6cqw,22px)" />
          <div style={{ flex: 1, display: "flex", padding: "3.4cqw", gap: "3.4cqw", minHeight: 0 }}>
            {/* left: photo */}
            <div style={{ width: "30%", display: "flex", flexDirection: "column" }}>
              <PhotoBox photo={photo} style={{ flex: 1 }} />
            </div>
            {/* right: title, fields, qr */}
            <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "2cqw" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontFamily: "'Playfair Display',serif",
                      fontWeight: 700,
                      color: K.ink,
                      fontSize: "clamp(13px,4.4cqw,26px)",
                      lineHeight: 1.04,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    Identification Card
                  </div>
                  <div
                    style={{
                      fontFamily: "Arial,Helvetica,sans-serif",
                      fontWeight: 700,
                      color: K.inkSoft,
                      fontSize: "clamp(6px,1.8cqw,11px)",
                      letterSpacing: "0.13em",
                      marginTop: "0.5cqw",
                    }}
                  >
                    NORTH YORKSHIRE MOORS RAILWAY
                  </div>
                </div>
                <NymrOval w="clamp(58px,20cqw,118px)" />
              </div>

              {/* fields on the left, QR pinned bottom-right — never overlaps */}
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "space-between",
                  gap: "3cqw",
                  marginTop: "2.5cqw",
                  minHeight: 0,
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: "1.4cqw", fontFamily: "Arial,Helvetica,sans-serif", minWidth: 0 }}>
                  <Fld label="Name:" value={holder.name} fs="clamp(8px,2.5cqw,15px)" />
                  <Fld label="ID No:" value={holder.idNo} fs="clamp(8px,2.5cqw,15px)" />
                  <Fld label="Card No:" value={groupCardNo(holder.cardNo)} fs="clamp(8px,2.5cqw,15px)" />
                  <Fld label="Issue Date:" value={fmtDate(holder.issueDate)} fs="clamp(8px,2.5cqw,15px)" />
                  <Fld label="Expiry:" value={expiryLabel(null)} fs="clamp(8px,2.5cqw,15px)" />
                  <div style={{ marginTop: "1cqw" }}>
                    <Contactless s="clamp(15px,4.4cqw,26px)" c={K.inkSoft} />
                  </div>
                </div>
                {hasCardNo(holder.cardNo) && (
                  <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: "1cqw" }}>
                    <QrCode value={qrVal} size={Math.round(cap * 0.2)} fg={K.ink} />
                    <div
                      style={{
                        fontFamily: "'IBM Plex Mono',monospace",
                        fontWeight: 700,
                        fontSize: "clamp(6px,1.7cqw,10px)",
                        letterSpacing: "0.12em",
                        color: K.inkSoft,
                      }}
                    >
                      {qrVal}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <Sheen />
      </div>
    );
  }

  // ─── PORTRAIT ───
  return (
    <div style={frame} onClick={onEnlarge} title={onEnlarge ? "Tap to enlarge" : undefined}>
      <div style={face}>
        <Band fs="clamp(15px,7cqw,30px)" />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "5cqw 6cqw 5.5cqw", minHeight: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "3cqw", marginBottom: "3.5cqw" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: "'Playfair Display',serif", fontWeight: 700, color: K.ink, fontSize: "clamp(15px,6.2cqw,24px)", lineHeight: 1.03 }}>
                Identification Card
              </div>
              <div
                style={{
                  fontFamily: "Arial,Helvetica,sans-serif",
                  fontWeight: 700,
                  color: K.inkSoft,
                  fontSize: "clamp(6px,2.6cqw,10px)",
                  letterSpacing: "0.09em",
                  marginTop: "1cqw",
                }}
              >
                NORTH YORKSHIRE MOORS RAILWAY
              </div>
            </div>
            <NymrOval w="clamp(54px,22cqw,92px)" />
          </div>

          <PhotoBox photo={photo} style={{ width: "44%", aspectRatio: "1/1.12", alignSelf: "center", marginBottom: "3.5cqw" }} />

          <div style={{ display: "flex", flexDirection: "column", gap: "1.9cqw", fontFamily: "Arial,Helvetica,sans-serif" }}>
            <Fld label="Name:" value={holder.name} fs="clamp(10px,3.5cqw,15px)" />
            <Fld label="ID No:" value={holder.idNo} fs="clamp(10px,3.5cqw,15px)" />
            <Fld label="Card No:" value={groupCardNo(holder.cardNo)} fs="clamp(10px,3.5cqw,15px)" />
            <Fld label="Issue Date:" value={fmtDate(holder.issueDate)} fs="clamp(10px,3.5cqw,15px)" />
            <Fld label="Expiry:" value={expiryLabel(null)} fs="clamp(9px,3.1cqw,14px)" />
          </div>

          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "3cqw", marginTop: "auto", paddingTop: "3.5cqw" }}>
            <Contactless s="clamp(18px,6cqw,28px)" c={K.inkSoft} />
            {hasCardNo(holder.cardNo) && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "1cqw" }}>
                <QrCode value={qrVal} size={Math.round(cap * 0.28)} fg={K.ink} />
                <div
                  style={{
                    fontFamily: "'IBM Plex Mono',monospace",
                    fontWeight: 700,
                    fontSize: "clamp(7px,2.4cqw,11px)",
                    letterSpacing: "0.14em",
                    color: K.inkSoft,
                  }}
                >
                  {qrVal}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Sheen />
    </div>
  );
}
